/**
 * Cart minimum-order-quantity (MOQ) and case-increment enforcement — AC-125 (cart half).
 *
 * Server-rendered markers (snippets/cart-products.liquid):
 * - each cart line row carries `data-min-qty` and `data-qty-increment`, both resolved by
 *   snippets/moq-rules.liquid (custom.minimum_quantity and pricing.quantity_increment)
 * - rows with a MOQ > 1 contain a hidden `[data-moq-error]` message element
 * - rows with an increment > 1 contain a hidden `[data-moq-increment-error]` element
 * - hidden helper / fee lines carry `data-moq-exempt` and are never quantity-validated
 *   (see snippets/cart-moq-exempt.liquid for the allow-list and the reasoning)
 *
 * `pricing.quantity_increment` has no metafield definition in Shopify yet, so every line
 * renders `data-qty-increment="1"` today, no increment message element exists, and the
 * increment rule is inert — the cart behaves exactly as the minimum-only build did.
 *
 * When any line fails a rule, its warning is shown and every checkout route is blocked.
 * Blocked routes are marked with `data-moq-blocked` so that a control which Liquid (or
 * another script) had already disabled for a different reason is never taken over, and so
 * that a link-styled route can be styled as blocked. See `syncBlockedState` for what the
 * marker does and does not decide.
 *
 * Accessibility: the offending quantity input is marked `aria-invalid` and pointed at its
 * warning with `aria-describedby`, and the disabled checkout button — which leaves the tab
 * order — has a visible reason next to it (`[data-moq-checkout-reason]`, rendered by
 * snippets/cart-summary.liquid) that it is also `aria-describedby`.
 *
 * The per-row and per-button messages are deliberately NOT live regions. `role="alert"` is an
 * implicit `aria-live="assertive"`, and every cart morph restores the server markup (warnings
 * hidden, checkout re-enabled) before this module flips them back — so a live region re-fired
 * on every single re-render, for every invalid row, for as long as the customer was trying to
 * fix the problem. Their state is announced where it is actionable instead: on the control
 * the customer is standing on. The single exception is one polite, transition-only summary
 * region, `checkoutBlockedAnnouncer` below, which exists because otherwise nothing at all is
 * announced at the moment checkout becomes unavailable.
 *
 * Morph resilience: Horizon re-renders the cart page and cart drawer through the Section
 * Rendering API (morphSection in component-cart-items.js), which replaces rows, error
 * elements, and checkout buttons. Instead of binding to the replaced nodes, this module
 * uses:
 * - delegated `change`/`input` listeners on `document` for quantity inputs
 * - a delegated capture-phase `click` listener on `document` for checkout routes that
 *   cannot carry the native `disabled` property (links, wallet containers)
 * - a delegated capture-phase `submit` listener on `document` as a backstop
 * - a debounced MutationObserver on `document.body` that re-validates after any cart
 *   morph, re-showing warnings and re-blocking checkout routes
 */

/**
 * Checkout routes that can carry the native `disabled` property. `button[name="checkout"]`
 * is Horizon's own cart-page and cart-drawer submit button (snippets/cart-summary.liquid);
 * the other two cover an input-styled submit or a form posting straight to checkout, which
 * an app block or a custom section can introduce.
 */
const CHECKOUT_SUBMIT_SELECTOR = [
  'button[name="checkout"]',
  'input[name="checkout"]',
  'form[action*="/checkout"] [type="submit"]',
].join(', ');

/**
 * Link-styled checkout routes. Not a `button`, so they cannot be disabled — they are
 * blocked by the click guard below plus `pointer-events: none`. The store's previous Dawn
 * theme guarded `a[href="/checkout"]` for exactly this reason. `*=` also catches localised
 * paths (`/en-ca/checkout`) and query strings; the exclusion is the customer-login route
 * `/tools/checkout/front_end/login`, which is a sign-in page and not a checkout, and is the
 * same exclusion the Zakeke fee-sync gate in layout/theme.liquid uses.
 *
 * KNOWN RESIDUAL (measured 2026-07-27, not a regression — the same shape exists on f935c64).
 * An anchor this module has blocked whose `href` is then rewritten OUT of this selector by a
 * third-party actor is never visited by `syncCheckoutRoutes` again, so it keeps
 * `aria-disabled="true"`, keeps `data-moq-blocked`, and keeps `pointer-events: none` for the
 * life of the page — even after the cart becomes valid. It cannot reach the store's primary
 * control, which is `button[name="checkout"]`, and no app was found that rewrites such an
 * href; closing it would mean tracking blocked anchors by node rather than by selector.
 */
const CHECKOUT_LINK_SELECTOR = 'a[href*="/checkout"]:not([href*="/tools/checkout/front_end/login"])';

/**
 * The accelerated (wallet) checkout container on the CART — Shop Pay, PayPal, Google Pay,
 * Apple Pay, rendered by `content_for_additional_checkout_buttons`.
 *
 * KNOWN, ACCEPTED BYPASS. These buttons live inside a Shopify-owned custom element whose
 * real controls are in a cross-origin frame, so their click CANNOT be intercepted from
 * theme JavaScript: no theme listener ever sees the event, and the resulting navigation
 * cannot be cancelled. A customer can therefore reach checkout past a below-minimum cart
 * line through a wallet button. Only a Cart & Checkout Validation Function, which runs
 * server-side on Shopify, can close this.
 *
 * Owner ruling 2026-07-27: DROP THE MITIGATION, document the bypass. This module used to
 * make the container non-interactive (`inert` + `pointer-events: none`), which proved
 * net-negative: `morph.js` refuses to morph `shopify-accelerated-checkout-cart` at all, and
 * the ownership marker is dropped the instant any other actor writes a blocking property,
 * so the element could be stranded permanently `inert` while still looking fully enabled —
 * a dead checkout button with no recovery short of a page reload. A silently dead wallet
 * button costs real orders from customers doing nothing wrong; the bypass it prevented only
 * reaches customers who are deliberately or accidentally under-ordering.
 *
 * The container is still watched by the capture-phase click guard below. That guard is
 * stateless — it cancels an event or it does not — so it can never leave a control stranded,
 * and it does still cover a same-origin button an app may render inside the wrapper. It
 * cannot see the Shopify element's own clicks, which is the bypass above.
 *
 * Deliberately scoped to the cart element: `shopify-accelerated-checkout` (no `-cart`) is
 * the product-page Buy-it-now button, which checks out a single product without touching
 * the cart, so cart line state must not gate it.
 */
const ACCELERATED_CHECKOUT_SELECTOR = ['.additional-checkout-buttons', 'shopify-accelerated-checkout-cart'].join(', ');

/** Every route the capture-phase click guard watches. */
const CHECKOUT_CLICK_SELECTOR = [
  CHECKOUT_SUBMIT_SELECTOR,
  CHECKOUT_LINK_SELECTOR,
  ACCELERATED_CHECKOUT_SELECTOR,
].join(', ');

const QUANTITY_INPUT_SELECTOR = 'input[name="updates[]"]';
const ROW_SELECTOR = '[data-min-qty]';
const VISIBLE_ERROR_SELECTOR = '[data-moq-error]:not([hidden]), [data-moq-increment-error]:not([hidden])';

/**
 * The summary-level reason element rendered next to the checkout button
 * (snippets/cart-summary.liquid). A blocked checkout button carries the real `disabled`
 * property, which takes it out of the tab order entirely, so there is no control left for a
 * keyboard or screen-reader user to land on and no reason announced. This element is that
 * reason: revealed when the cart is blocked, and pointed at by the button's
 * `aria-describedby` so the association survives for assistive technology browsing the page.
 */
const CHECKOUT_REASON_SELECTOR = '[data-moq-checkout-reason]';

/**
 * The one polite live region on the page, so that a screen-reader user is told AT THE MOMENT
 * checkout becomes unavailable rather than only on arriving at the control.
 *
 * Exactly one region, and written only on the transition INTO the blocked state. The four
 * `role="alert"` regions this replaces re-announced assertively on every single cart morph,
 * because each morph restores the server markup (warnings hidden, checkout enabled) and this
 * module immediately flips it back. Two things prevent that here: the region is created once
 * and lives on `document.body`, outside every section the Section Rendering API morphs, so no
 * re-render replaces it; and `announcedBlocked` is module state, which a morph does not
 * touch, so a re-render finds nothing to announce. Clearing on the way back to a valid cart
 * is a removal, which live regions do not announce.
 */
const checkoutBlockedAnnouncer = document.createElement('p');
checkoutBlockedAnnouncer.className = 'visually-hidden';
checkoutBlockedAnnouncer.setAttribute('aria-live', 'polite');
document.body.append(checkoutBlockedAnnouncer);

let announcedBlocked = false;

/**
 * Announces a newly blocked checkout, once per transition. The wording is read from the
 * visible reason element so there is a single source of that copy (Liquid).
 * @param {boolean} valid
 */
function announceCheckoutBlocked(valid) {
  const blocked = !valid;
  if (blocked === announcedBlocked) return;

  const reason = document.querySelector(CHECKOUT_REASON_SELECTOR);
  const text = blocked ? (reason?.textContent ?? '').trim() : '';

  // Nothing to say yet — no reason element on the page, or it is empty. Leave `announcedBlocked`
  // UNLATCHED so that a reason element which morphs in later still produces the one transition
  // announcement; latching here would suppress it silently for the life of the page.
  if (blocked && !text) return;

  checkoutBlockedAnnouncer.textContent = text;
  announcedBlocked = blocked;
}

/** Sequence for generated ids, so `aria-describedby` never depends on server-side ids. */
let describedBySequence = 0;

/**
 * Adds or removes one token from an element's `aria-describedby`, leaving any token this
 * module did not add in place.
 * @param {Element} element
 * @param {string} id
 * @param {boolean} describe
 */
function setDescribedBy(element, id, describe) {
  const tokens = (element.getAttribute('aria-describedby') ?? '')
    .split(/\s+/)
    // Drop our own token, plus any `moq-` token whose element a Section Rendering API morph
    // has since removed — otherwise dangling ids accumulate on the input without bound.
    .filter((token) => token && token !== id && (!token.startsWith('moq-') || document.getElementById(token)));

  if (describe) tokens.push(id);

  if (tokens.length) element.setAttribute('aria-describedby', tokens.join(' '));
  else element.removeAttribute('aria-describedby');
}

/**
 * Points an element's `aria-describedby` at a message element while that message is showing.
 * @param {Element | null} element
 * @param {Element | null} message
 * @param {boolean} showing
 */
function describeWith(element, message, showing) {
  if (!element || !message) return;

  // Mint an id only when the message is actually being pointed at: morph keys nodes by id,
  // so stamping generated ids on an error-free cart is churn on every render.
  if (showing && !message.id) message.id = `moq-message-${(describedBySequence += 1)}`;
  setDescribedBy(element, message.id, showing);
}

/**
 * Reads an integer quantity-rule attribute. A missing, malformed or below-1 value means
 * "no rule", which is expressed as 1 — the dominant case: a `custom.minimum_quantity` of 1
 * is "no minimum" and must never be treated as an enforced floor, and
 * `pricing.quantity_increment` is absent on every product today.
 * @param {string | null} value
 * @returns {number}
 */
function readRule(value) {
  const parsed = parseInt(value ?? '1', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

/** The blocking properties a foreign actor could write to a control this module blocked. */
const BLOCK_ATTRIBUTE_FILTER = ['disabled', 'aria-disabled', 'inert'];

/**
 * Relinquishes this module's claim on any marked control that a foreign actor has written a
 * blocking property to. See `syncBlockedState` for why.
 * @param {MutationRecord[]} records
 */
function relinquishForeignBlocks(records) {
  for (const record of records) {
    const { target } = record;
    if (target instanceof Element && target.hasAttribute('data-moq-blocked')) {
      target.removeAttribute('data-moq-blocked');
    }
  }
}

const blockOwnershipObserver = new MutationObserver(relinquishForeignBlocks);

/**
 * The controls this module actually blocked, held where no foreign DOM write can erase it.
 * The `data-moq-blocked` attribute cannot serve this purpose: it is dropped the moment
 * another actor writes a blocking property, including a no-op `disabled = true`.
 */
const ownedBlocks = new WeakSet();

/**
 * Applies or clears the blocked state on one checkout route.
 *
 * The `data-moq-blocked` marker is the invariant on the BLOCKING side: a control that Liquid
 * or another script had already blocked is never marked, so we never take it over and never
 * release it. A control we marked is un-marked the instant another actor writes a blocking
 * property to it, so we stop re-asserting a block on it — but see the releasing branch, which
 * still lets go of a control we are the ones holding down.
 *
 * Ordering is the whole trick. The queue is drained FIRST, so the relinquish decision is
 * already made by the time the BLOCKING branch reads the marker; draining after that branch
 * had chosen was the previous bug. Setting an already-set boolean property still queues a
 * mutation record, so even a no-op second `disabled = true` is observed.
 *
 * The RELEASING branch reads `ownedBlocks` instead, so it is not gated on the marker at all.
 * Owner ruling 2026-07-27: prefer the benign failure. Gating the release on the marker
 * stranded OUR OWN block on a cart the customer had since corrected — a dead Checkout
 * button with the reason hidden, plus page-wide `/checkout` anchors that no cart morph
 * reaches, so they kept `aria-disabled` for the life of the page. Wrongly re-enabling a
 * control somebody else disabled is recoverable and low-harm; wrongly leaving Checkout dead
 * on a valid cart costs real orders. We therefore always let go of what we are holding down.
 *
 * The marker alone cannot express this: `relinquishForeignBlocks` runs from an asynchronous
 * observer callback, so by the next validation pass the attribute is already gone. That is
 * why ownership lives in a WeakSet and the attribute is left to do the two jobs a foreign
 * actor may legitimately veto — re-asserting a block, and styling.
 *
 * @param {Element} control
 * @param {boolean} blocked
 * @param {(control: any) => boolean} isAlreadyBlocked - true when the control is already
 *   blocked, whoever blocked it. Marking is skipped in that case.
 * @param {(control: any, blocked: boolean) => void} apply
 */
function syncBlockedState(control, blocked, isAlreadyBlocked, apply) {
  // Anything queued up to this point is, by definition, someone else's write.
  relinquishForeignBlocks(blockOwnershipObserver.takeRecords());

  if (blocked) {
    if (control.hasAttribute('data-moq-blocked')) return;
    if (isAlreadyBlocked(control)) return;
    blockOwnershipObserver.observe(control, { attributes: true, attributeFilter: BLOCK_ATTRIBUTE_FILTER });
    apply(control, true);
    blockOwnershipObserver.takeRecords(); // Discard the record our own write just queued.
    ownedBlocks.add(control);
    control.setAttribute('data-moq-blocked', '');
  } else if (ownedBlocks.has(control)) {
    apply(control, false);
    blockOwnershipObserver.takeRecords();
    ownedBlocks.delete(control);
    control.removeAttribute('data-moq-blocked');
  }
}

/**
 * Blocks or releases every checkout route on the page.
 * @param {boolean} valid - true when every cart line passes its quantity rules.
 */
function syncCheckoutRoutes(valid) {
  announceCheckoutBlocked(valid);

  // Announce WHY checkout is unavailable. The button itself is genuinely `disabled`, which
  // is the strongest gate but removes it from the tab order, so the reason has to live in a
  // control the customer can still reach.
  //
  // Hide every reason first, then reveal only beside a control this module is actually the one
  // holding down. A control somebody else disabled (Liquid's empty-cart gate, an app) is not
  // ours to explain — "corrected or removed" would be a wrong explanation of their block — and
  // the blanket hide also stops a reason whose control has since been morphed away from being
  // left standing on a valid cart.
  for (const reason of document.querySelectorAll(CHECKOUT_REASON_SELECTOR)) {
    if (reason instanceof HTMLElement) reason.hidden = true;
  }

  for (const control of document.querySelectorAll(CHECKOUT_SUBMIT_SELECTOR)) {
    syncBlockedState(
      control,
      !valid,
      (submit) => submit.disabled,
      (submit, blocked) => {
        submit.disabled = blocked;
      }
    );

    // Read ownership AFTER the sync, so this is "we blocked it", not "it is blocked".
    const owned = ownedBlocks.has(control);
    const reason = control.closest('.cart__ctas')?.querySelector(CHECKOUT_REASON_SELECTOR) ?? null;
    if (reason instanceof HTMLElement) reason.hidden = !owned;
    describeWith(control, reason, owned);
  }

  for (const link of document.querySelectorAll(CHECKOUT_LINK_SELECTOR)) {
    syncBlockedState(
      link,
      !valid,
      (anchor) => anchor.getAttribute('aria-disabled') === 'true',
      (anchor, blocked) => {
        if (blocked) anchor.setAttribute('aria-disabled', 'true');
        else anchor.removeAttribute('aria-disabled');
      }
    );
  }

  // No wallet-container state is written here — see ACCELERATED_CHECKOUT_SELECTOR.
}

/**
 * Drops the invalid state from a row's quantity input — used for rows that are not
 * validated at all, so a row that becomes exempt (or loses its rule) between renders never
 * keeps a stale `aria-invalid`.
 * @param {Element} row
 */
function clearInputError(row) {
  const input = row.querySelector(QUANTITY_INPUT_SELECTOR);
  if (!input) return;

  input.removeAttribute('aria-invalid');
  describeWith(input, row.querySelector('[data-moq-error]'), false);
  describeWith(input, row.querySelector('[data-moq-increment-error]'), false);
}

/**
 * Validates every cart line against its MOQ and case increment, and syncs the UI state.
 * @returns {boolean} true when every line meets both rules.
 */
function validateCartMoq() {
  let valid = true;

  for (const row of document.querySelectorAll(ROW_SELECTOR)) {
    const minError = row.querySelector('[data-moq-error]');
    const incrementError = row.querySelector('[data-moq-increment-error]');

    // Hidden helper / fee lines: never quantity-validated, and never left showing a stale
    // warning if a product moves onto the exempt list between renders.
    if (row.hasAttribute('data-moq-exempt')) {
      row.classList.remove('cart-items__row--moq-invalid');
      if (minError) minError.hidden = true;
      if (incrementError) incrementError.hidden = true;
      clearInputError(row);
      continue;
    }

    const minQty = readRule(row.getAttribute('data-min-qty'));
    const increment = readRule(row.getAttribute('data-qty-increment'));
    if (minQty <= 1 && increment <= 1) {
      clearInputError(row);
      continue;
    }

    const input = row.querySelector(QUANTITY_INPUT_SELECTOR);
    if (!input) continue;

    const currentQty = parseInt(input.value, 10) || 0;
    const belowMoq = minQty > 1 && currentQty < minQty;
    const offIncrement = increment > 1 && currentQty % increment !== 0;

    if (belowMoq || offIncrement) valid = false;
    row.classList.toggle('cart-items__row--moq-invalid', belowMoq || offIncrement);
    if (minError) minError.hidden = !belowMoq;
    if (incrementError) incrementError.hidden = !offIncrement;

    // Announce the failure on the control that caused it, not only in the message element.
    if (belowMoq || offIncrement) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
    describeWith(input, minError, belowMoq);
    describeWith(input, incrementError, offIncrement);
  }

  syncCheckoutRoutes(valid);

  return valid;
}

/** Brings the first visible quantity warning into view after a blocked checkout attempt. */
function revealFirstError() {
  const firstError = document.querySelector(VISIBLE_ERROR_SELECTOR);
  firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * @param {(...args: any[]) => void} fn
 * @param {number} wait
 */
function debounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), wait);
  };
}

const debouncedValidate = debounce(validateCartMoq, 100);

// Delegated quantity listeners — survive Section Rendering API morphs.
document.addEventListener('change', (event) => {
  if (event.target instanceof Element && event.target.matches(QUANTITY_INPUT_SELECTOR)) debouncedValidate();
});
document.addEventListener('input', (event) => {
  if (event.target instanceof Element && event.target.matches(QUANTITY_INPUT_SELECTOR)) debouncedValidate();
});

// Click guard for the checkout routes that cannot be disabled (links, and the wallet
// container as belt-and-braces). Delegated and capture-phase, so it survives cart morphs
// and runs before the route's own handler.
//
// Ordering note: the Zakeke fee-sync gate in layout/theme.liquid registers its own
// capture-phase click listener first (it is an inline script; this is a module). On a real
// user click it calls stopImmediatePropagation and re-clicks the control asynchronously
// with `data-zakeke-handled`. On that second, synthetic click it returns early WITHOUT
// stopping propagation, so this guard still sees the event and can cancel the navigation.
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(CHECKOUT_CLICK_SELECTOR)) return;
    if (validateCartMoq()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    revealFirstError();
  },
  true
);

// Backstop: block cart form submission (covers Enter-key submits and any checkout path
// that bypasses the blocked-control state).
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id !== 'cart-form' && !(form.getAttribute('action') || '').includes('/checkout')) return;
    if (validateCartMoq()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    revealFirstError();
  },
  true
);

// Re-validate after cart morphs: morphSection replaces rows, quantity warnings, and
// checkout buttons, wiping the state applied above. A debounced body-wide observer is
// cheap (the callback only schedules the debounced validator) and catches every re-render
// path, including external app cart updates.
//
// The attribute filter closes a MEASURED gap (2026-07-28, headless Chromium): morph is a
// diff, so a re-render that only restores ATTRIBUTES — the row's `hidden` warning, the
// invalid class, the checkout button's `disabled`, and the input's `value` — produces no
// childList records at all, and a childList-only observer never learns the cart changed.
// The state this module had applied was then silently dropped and checkout was left
// ENABLED on a below-minimum cart until the customer touched something.
//
// Every attribute named here is one this module NEVER writes (it writes `hidden`,
// `disabled`, `aria-invalid`, `aria-describedby` and `data-moq-blocked`), so its own
// output cannot feed this observer and the pass cannot self-perpetuate.
//
// `characterData` closes the REMAINING measured gap (D-1, 2026-07-28, headless Chromium).
// morph.js applies text updates as `oldNode.nodeValue = newNode.nodeValue` (assets/morph.js
// :311-312), which is neither a childList nor an attribute record, and it short-circuits an
// unchanged subtree entirely (:236-238). So a re-render in which only money strings, totals
// or counts changed — an applied discount code, a cart-note edit, an app-triggered refresh —
// produced ZERO records here, this module never re-validated, and the customer was left
// looking at an enabled Checkout button and no warning on a below-minimum cart.
//
// Scope: body-wide with `subtree`, deliberately, and NOT narrowed to a cart container. The
// cart section itself is what morph replaces, so anything narrower has to be re-bound after
// every re-render — the failure mode this whole observer exists to avoid. The cost was
// measured rather than assumed: the callback does nothing but reschedule a 100ms debounce,
// so a burst of N text mutations still runs `validateCartMoq` once (measured: 60 text
// mutations across 6 morphs -> 6 observer callbacks -> 1 validation pass per morph).
//
// It cannot self-perpetuate: the one text this module writes is `checkoutBlockedAnnouncer`
// via `textContent`, which is a childList mutation (already observed before this change),
// and the pass it schedules early-returns in `announceCheckoutBlocked` because the blocked
// state has not changed.
const observer = new MutationObserver(() => {
  debouncedValidate();
});
observer.observe(document.body, {
  childList: true,
  subtree: true,
  characterData: true,
  attributes: true,
  attributeFilter: ['value', 'data-min-qty', 'data-qty-increment', 'data-moq-exempt'],
});

validateCartMoq();
